<template>
  <section class="stat-cards" :aria-label="label" :aria-busy="loading ? 'true' : 'false'">
    <div v-for="stat in stats" :key="stat.key" class="stat-card stat-cards__card">
      <div class="stat-cards__head">
        <span class="stat-card__label">{{ stat.label }}</span>
        <span
          v-if="stat.icon"
          class="stat-cards__icon"
          :class="`stat-cards__icon--${stat.tone || 'primary'}`"
          aria-hidden="true"
        >
          <i class="pi" :class="stat.icon"></i>
        </span>
      </div>
      <div class="stat-cards__figure">
        <span class="stat-card__value">
          <span v-if="loading" class="stat-cards__skeleton stat-cards__skeleton--value" aria-hidden="true"></span>
          <template v-else>{{ stat.value }}</template>
        </span>
        <!-- The period's history, once the numbers are in. -->
        <sparkline
          v-if="!loading && Array.isArray(stat.trend) && stat.trend.length > 1"
          class="stat-cards__trend"
          :values="stat.trend"
          :color="stat.trendColor"
        />
      </div>
      <span
        v-if="loading || stat.delta"
        class="stat-card__delta stat-cards__delta"
        :class="!loading && stat.deltaTone ? `stat-card__delta--${stat.deltaTone}` : ''"
        :title="loading ? null : stat.delta"
      >
        <span v-if="loading" class="stat-cards__skeleton stat-cards__skeleton--delta" aria-hidden="true"></span>
        <template v-else>{{ stat.delta }}</template>
      </span>
      <span v-if="!loading && stat.note" class="stat-cards__note" :title="stat.note">{{ stat.note }}</span>
    </div>
  </section>
</template>

<script>
import sparkline from '@/components/charts/Sparkline.vue';

// A row of the theme's `.stat-card`s (label, value, delta) with an icon, as
// the frontend's list pages show above their tables. Each stat is
// `{ key, label, value, delta?, deltaTone?: 'up' | 'down' | 'info', note?,
//   trend?: number[], trendColor?, icon?, tone? }`, where `tone` tints the
// icon (primary, success, danger or warning), `note` is a muted second line
// and `trend` draws a sparkline beside the value.
export default {
  name: 'StatCards',
  components: {
    sparkline,
  },
  props: {
    stats: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'Overview',
    },
  },
};
</script>

<style scoped>
.stat-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  flex-shrink: 0;
}

.stat-cards__card {
  min-width: 0;
  min-height: 118px;
}

.stat-cards__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 30px;
}

.stat-cards__head .stat-card__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The same tints as the status pills, so a card reads like the rows it counts. */
.stat-cards__icon {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-md);
  font-size: 0.85rem;
}

.stat-cards__icon--primary { background: var(--color-primary-soft); color: var(--color-primary-hover); }
.stat-cards__icon--success { background: #E8F8F0; color: #0E9F6E; }
.stat-cards__icon--danger  { background: #FEECEC; color: #C81E1E; }
.stat-cards__icon--warning { background: #FFF4E5; color: #C2680B; }

.stat-cards__figure {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.stat-cards__figure .stat-card__value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-cards__trend {
  margin-bottom: 2px;
}

.stat-cards__delta,
.stat-cards__note {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-card__delta.stat-card__delta--down {
  color: #C81E1E;
}

.stat-cards__note {
  margin-top: -2px;
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
}

/* ------------------------------- skeleton ------------------------------- */
/* The bars sit inside the value and delta lines (sized in `em` to stay within
   them), so a card keeps its height when the numbers land. */
.stat-cards__skeleton {
  display: inline-block;
  vertical-align: top;
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7 0%, #f8fafc 45%, #eef2f7 100%);
  background-size: 220% 100%;
  animation: stat-cards-shimmer 1.15s ease-in-out infinite;
}

.stat-cards__skeleton--value {
  width: 56px;
  height: 0.8em;
  margin-top: 0.15em;
  border-radius: var(--radius-sm);
}

.stat-cards__skeleton--delta {
  width: 60%;
  height: 0.8em;
  margin-top: 0.35em;
}

@keyframes stat-cards-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .stat-cards__skeleton {
    animation: none;
  }
}

/* ------------------------------ responsive ------------------------------ */
@media (max-width: 1100px) {
  .stat-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Phones: a horizontal, snap-scrolling rail that peeks the next card, as on
   the dashboard. It runs to the screen edges so cards slide out of view
   instead of being cut off by the page padding. */
@media (max-width: 575.98px) {
  .stat-cards {
    display: flex;
    gap: 10px;
    margin: 0 -14px;
    padding: 0 14px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding: 0 14px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .stat-cards::-webkit-scrollbar {
    display: none;
  }

  .stat-cards__card {
    flex: 0 0 78%;
    max-width: 280px;
    scroll-snap-align: start;
  }
}
</style>
